import desktop1024Rail from "@/assets/rhythm-rail/murtada-rhythm-desktop-1024.svg";
import desktop1440Rail from "@/assets/rhythm-rail/murtada-rhythm-desktop-1440.svg";
import mobileRail from "@/assets/rhythm-rail/murtada-rhythm-mobile.svg";
import tabletRail from "@/assets/rhythm-rail/murtada-rhythm-tablet.svg";

const RAIL_ASSETS = {
  mobile: mobileRail,
  tablet: tabletRail,
  desktop1024: desktop1024Rail,
  desktop1440: desktop1440Rail,
} as const;

/**
 * Segment 03's terminus, in the rail's own joint vocabulary: a fixed arc into
 * 45 degrees, a straight diagonal, a fixed arc back onto axis. Only the
 * diagonal's length varies, which is exactly what the approved Figma vectors
 * do (158:5, 162:8, 162:25). Every pipe continues through the rhythm section's
 * bottom seam as one overflow-visible SVG stroke.
 *
 * These cannot be baked into the exported rail SVGs. Those are `width: 100vw`,
 * so a fixed viewBox x tracks the viewport, and the terminus has to track the
 * content column instead. Each variant below is a fixed-geometry pipe.
 */
const TERMINUS_PIPES = [
  {
    // Mobile: rail lime at x 92, spine at x 21. One 71px return, then straight
    // down to the section boundary. Figma 162:8.
    frame: "mobile",
    viewBox: "0 0 81 284",
    width: 10,
    d: "M76 0L76 193C76 199.667 73.643 205.357 68.929 210.071L12.071 266.929C7.357 271.643 5 277.333 5 284V100000",
  },
  {
    // Tablet at 40-48rem: #path is inset 40px, so the return is 119px.
    frame: "tablet-narrow",
    viewBox: "0 0 131 246",
    width: 12,
    d: "M125 0L125 103C125 111 122.17 117.83 116.51 123.49L14.49 225.51C8.83 231.17 6 238 6 246V100000",
  },
  {
    // Tablet at 48-64rem: #path is inset 48px, so the return is 111px. Figma 162:25.
    frame: "tablet",
    viewBox: "0 0 123 246",
    width: 12,
    d: "M117 0L117 111C117 119 114.17 125.83 108.51 131.49L14.49 225.51C8.83 231.17 6 238 6 246V100000",
  },
  {
    frame: "desktop1024",
    viewBox: "0 0 41 41",
    width: 10,
    d: "M0 5C6.545 5 12.136 7.316 16.765 11.947L30.69 25.872C33.685 28.867 36 34.456 36 41V100000",
  },
  {
    frame: "desktop1440",
    viewBox: "0 0 50 50",
    width: 12,
    d: "M0 6C8 6 14.83 8.83 20.49 14.49L37.51 31.51C41.17 35.17 44 42 44 50V100000",
  },
] as const;

/**
 * Every responsive rail is the exact Figma-exported vector. CSS only selects
 * the matching source frame and preserves its intrinsic aspect ratio. The
 * terminus that follows segment 03 is drawn here rather than exported, because
 * it has to land on the content column and the exports scale with the viewport.
 */
export function RhythmRail() {
  return (
    <div aria-hidden="true" className="rhythm-rail">
      {Object.entries(RAIL_ASSETS).map(([frame, asset]) => (
        <img
          key={frame}
          src={asset}
          alt=""
          className={`rhythm-rail__frame rhythm-rail__frame--${frame}`}
        />
      ))}
      <div className="rhythm-rail__continuation">
        <img src={mobileRail} alt="" />
      </div>
      <div className="rhythm-rail__terminus">
        {/*
          Stroked, not a filled box, and for the same reason the descender is:
          this run has a stroke on either side of it -- the export's green at the
          colour boundary, the pipe's lime where it turns -- and a box snaps a
          fractional height to the nearest whole pixel while a stroke keeps it.
          The viewBox stretches because only the thickness has to be exact; a
          horizontal line's thickness is its y scale alone.
        */}
        <svg
          className="rhythm-rail__terminus-run"
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line x1="0" y1="0.5" x2="1" y2="0.5" stroke="currentColor" strokeWidth={1} />
        </svg>
        {TERMINUS_PIPES.map((pipe) => (
          <svg
            key={pipe.frame}
            className={`rhythm-rail__terminus-pipe rhythm-rail__terminus-pipe--${pipe.frame}`}
            viewBox={pipe.viewBox}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d={pipe.d}
              stroke="currentColor"
              strokeWidth={pipe.width}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ))}
      </div>
    </div>
  );
}
